import { DeletedTickets } from './pages/DeletedTickets.js';
import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useOutletContext } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './index.css';
import {
  api,
  pickDevIdentity,
  session,
  setSessionLostHandler,
  type DevUser,
} from './lib/api.js';
import { authConfig, signIn, signInAgain } from './lib/sso.js';
import { initTelemetry, pageView, setTelemetryAuth, trackError } from './lib/telemetry.js';
import { Shell } from './components/Shell.js';
import { landingFor } from './lib/access.js';
import { AppErrorBoundary } from './components/AppErrorBoundary.js';
import { Queue } from './pages/Queue.js';
import { TicketPage } from './pages/Ticket.js';
import { Dashboard } from './pages/Dashboard.js';
import { MixAdjustedPage } from './pages/MixAdjusted.js';
import { DaylightPage } from './pages/Daylight.js';
import { Comparison } from './pages/Comparison.js';
import { Knowledge } from './pages/Knowledge.js';
import { MyRequests } from './pages/MyRequests.js';
import { NewRequest } from './pages/NewRequest.js';
import { IntakePage } from './pages/Intake.js';
import { AdminPage } from './pages/Admin.js';
import { SearchPage } from './pages/Search.js';
import { Button, Callout, Spinner, ToastProvider, TooltipProvider } from './ui/index.js';

/**
 * How fresh what you are looking at has to be.
 *
 * A service desk is several people working one queue. If somebody resolves a
 * ticket and it drops out of "Open", every other screen showing that list is
 * now wrong — and nothing here refetched, so it stayed wrong until the page
 * was reloaded. Two agents picking up the same ticket because one of them
 * was reading a list from twenty minutes ago is the outcome that matters.
 *
 *   refetchOnWindowFocus   the big one. Agents live in Outlook and Teams and
 *                          come back to this tab constantly; coming back is
 *                          exactly when stale data gets acted on. It was off.
 *
 *   refetchInterval        bounds staleness for a screen somebody is staring
 *                          at without touching — a wallboard, a queue left
 *                          open while working a ticket.
 *
 *   ...InBackground false  a hidden tab polls nothing. Otherwise every tab
 *                          anybody ever left open keeps asking, forever.
 *
 * This bounds staleness; it does not eliminate it. A change still takes up
 * to the interval to appear, which is why a push channel is the next step
 * rather than a shorter interval.
 */
const qc = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: 1,
      refetchOnWindowFocus: true,
      refetchInterval: 30_000,
      refetchIntervalInBackground: false,
    },
  },
});

/**
 * Sign-in gate.
 *
 * The SERVER decides how people authenticate, and this asks it rather than
 * reading a build flag — one bundle for every environment, and no way for
 * the two halves to disagree about what a valid token looks like.
 *
 * Under Entra this hands off to MSAL and usually never returns, because the
 * browser is on its way to Microsoft. Under the dev provider it adopts a
 * seeded identity so local work opens straight into the queue.
 */
/**
 * What to show when sign-in fails.
 *
 * A network failure says "Failed to fetch" and needs the hint about the API.
 * Anything else — an AADSTS code, a seeding problem — already says what is
 * wrong, and appending a guess about an unrelated service sends the reader
 * to check something healthy.
 */
function explain(err: unknown): string {
  if (!(err instanceof Error)) return 'Could not reach the API.';

  // The shape a dead or unreachable server produces. Distinguished by the
  // message because fetch gives no status to read.
  const networkish = /failed to fetch|networkerror|load failed|econnrefused/i.test(err.message);
  if (networkish) return `${err.message}. Is the API running on port 5580?`;

  // Entra's own codes are precise and worth surfacing untouched; the code is
  // what somebody searches for or takes to an administrator.
  return err.message;
}


/**
 * Where "/" actually goes.
 *
 * It was a hard redirect to /queue, which is the right answer for staff and
 * the wrong one for everybody else: a person with no role in any group — the
 * overwhelmingly common case, since every ESA employee can be the requester
 * of a ticket without holding a grant anywhere — landed on the queue and was
 * met with "Not available in this group" as the first thing the product ever
 * said to them.
 *
 * `landingFor` already encodes the rule and is already used when switching
 * group; the root route simply was not asking it.
 */
function Landing() {
  const { role } = useOutletContext<{ role: string | null | undefined }>();
  return <Navigate to={landingFor(role)} replace />;
}


function Boot() {
  // NOT seeded from the stored token.
  //
  // Painting the app the instant a token exists meant every page query fired
  // before MSAL had initialised — against a token from the previous session,
  // with no renewer registered yet to rescue it. The result was a hundred
  // 401s and "token rejected", to avoid a spinner that lasts one silent
  // token refresh. Correctness first; the spinner is the price and it is
  // small.
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signedOut, setSignedOut] = useState<'user' | 'expired' | null>(
    session.token ? null : session.signedOut,
  );

  // Any page can discover the session is unrecoverable. Only this component
  // can do the right thing about it.
  useEffect(() => {
    setSessionLostHandler(() => setSignedOut('expired'));
    return () => setSessionLostHandler(null);
  }, []);

  useEffect(() => {
    if (signedOut) return;
    void (async () => {
      try {
        const cfg = await authConfig();

        if (cfg.provider === 'entra') {
          // ON EVERY LOAD, not only the one where somebody signed in.
          //
          // This used to be skipped whenever a token was already stored,
          // which is every load after the first. MSAL never came up, so
          // nothing held the account or the refresh token, and the hour-old
          // token in localStorage could not be renewed by anything. Cheap
          // when already signed in: a silent acquire and no redirect.
          await signIn(cfg);
          setReady(Boolean(session.token));
          return;
        }

        // Dev: a token already in hand is the identity somebody chose from
        // the menu. Re-picking here would quietly undo that on every reload.
        if (session.token) {
          setReady(true);
          return;
        }

        const { users } = await api.get<{ users: DevUser[] }>('/api/v1/auth/dev-users');
        const preferred = pickDevIdentity(users);
        if (!preferred) {
          setError('No identities are seeded. Run: npm run seed -- --apply');
          return;
        }
        session.token = preferred.token;
        setReady(true);
      } catch (err) {
        setError(explain(err));
      }
    })();
    // Deliberately once per load. Re-running on `ready` is what skipped the
    // provider in the first place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedOut]);

  if (signedOut) {
    return (
      <div className="app-bg flex h-full items-center justify-center p-6">
        <div className="card-soft relative z-10 w-full max-w-sm rounded-lg p-6 text-center">
          <h1 className="text-sm font-semibold text-ink">
            {signedOut === 'expired' ? 'Your session has ended' : 'Signed out'}
          </h1>
          <p className="mt-2 text-xs leading-relaxed text-ink-secondary">
            {signedOut === 'expired'
              ? 'This happens after a long idle period, or when access changed. Nothing you had open was lost.'
              : 'You have been signed out of Tributary.'}
          </p>
          <Button
            variant="primary"
            size="sm"
            className="mt-4"
            onClick={() => {
              // Cleared first, so the boot effect signs in rather than
              // rendering this panel again.
              session.signedOut = null;
              session.token = null;
              setReady(false);
              void (async () => {
                const cfg = await authConfig().catch(() => null);
                // Entra: to Microsoft's sign-in page, not a re-run of the
                // attempt already made on this page (see signInAgain).
                if (cfg?.provider === 'entra') {
                  await signInAgain(cfg);
                  return;
                }
                setSignedOut(null);
              })();
            }}
          >
            Sign in
          </Button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-bg flex h-full items-center justify-center p-6">
        <div className="card-soft relative z-10 w-full max-w-md rounded-lg p-5">
          <h1 className="text-sm font-semibold">Cannot start</h1>
          <Callout tone="danger" className="mt-3">
            {error}
          </Callout>
          {/* THREE DIFFERENT FAILURES, and only one of them is about you.
              An AADSTS code needs an administrator, not a database restart.
              A 5xx means the server answered badly and is nobody's sign-in
              problem — this used to call it one, because it classified on
              the message text and anything that was not network-shaped fell
              through to "sign-in failure". The API being dead behind a dev
              proxy lands here, and telling somebody their sign-in failed
              while their server is down sends them to the wrong place
              entirely. */}
          {/failed to fetch|port 5580|seeded|request failed with 5\d\d/i.test(error) ? (
            <>
              <p className="mt-3 text-xs text-ink-secondary">
                {/request failed with 5\d\d/i.test(error)
                  ? 'The API answered with an error, or is not running behind the dev server. Bring the stack up with:'
                  : 'Bring the stack up with:'}
              </p>
              <pre className="mt-2 overflow-x-auto rounded-md border border-line bg-surface-sunken p-3 text-2xs leading-relaxed">
{`npm run infra:up
npm run migrate -- --apply
npm run seed -- --apply
npm run dev`}
              </pre>
            </>
          ) : (
            <>
              <p className="mt-3 text-xs text-ink-secondary">
                This is a sign-in failure, not a problem with the stack.
              </p>
              <Button
                variant="secondary"
                size="sm"
                className="mt-3"
                onClick={() => {
                  session.signedOut = null;
                  session.token = null;
                  window.location.href = '/';
                }}
              >
                Try signing in again
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner label="Signing in" />
      </div>
    );
  }

  return (
    <Routes>
      <Route element={<Shell />}>
        <Route path="/" element={<Landing />} />
        <Route path="/queue" element={<Queue />} />
        <Route path="/queue/deleted" element={<DeletedTickets />} />
        <Route path="/t/:tenant/:number" element={<TicketPage />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/reports/mix" element={<MixAdjustedPage />} />
        <Route path="/reports/daylight" element={<DaylightPage />} />
        <Route path="/comparison" element={<Comparison />} />
        <Route path="/knowledge" element={<Knowledge />} />
        <Route path="/my" element={<MyRequests />} />
        <Route path="/new" element={<NewRequest />} />
        <Route path="/intake" element={<IntakePage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="*" element={<Landing />} />
      </Route>
    </Routes>
  );
}

/**
 * One `page` event per navigation, not per render.
 *
 * Mounted once, inside the router, rather than read from a hook somewhere
 * inside `Shell` or a page: a component that reads `location` fires its
 * effect on every render of whatever subtree it lives in, and that subtree
 * re-renders for reasons that have nothing to do with navigating (a query
 * refetch, a tenant switch). `location.pathname` — not the full location —
 * is the effect's only dependency, so a query-string-only change (`?view=`,
 * `?tab=`) does not double-count the screen it belongs to.
 */
function RouteTelemetry() {
  const location = useLocation();
  useEffect(() => {
    pageView(location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  return null;
}

/**
 * ONE ROOT, EVEN ACROSS A HOT RELOAD.
 *
 * `createRoot` at module scope is called again every time Vite re-evaluates
 * this module, and React 18 then has two roots owning the same DOM node.
 * They fight: one removes a child the other is holding, which throws
 * `NotFoundError: Failed to execute 'removeChild'` — and then keeps throwing,
 * once per render, for the life of the tab. It reached 2,952 console errors
 * from a single warning nobody could see under them.
 *
 * Kept on `window` rather than in a module variable because the module
 * variable is exactly what HMR discards.
 */
const container = document.getElementById('root')!;
const w = window as unknown as {
  __esaRoot?: ReturnType<typeof createRoot>;
  __esaTelemetryBooted?: boolean;
};
w.__esaRoot ??= createRoot(container);

/**
 * GLOBAL ERROR CAPTURE, ONCE — the same hot-reload trap as the root above,
 * for a `window` listener instead of a DOM node. `window` is never recreated
 * across a Vite re-evaluation of this module, so an unguarded
 * `addEventListener` here would add a second pair on every reload, then a
 * third, and every real error would be reported two, three, four times
 * over — indistinguishable from the error rate actually doubling.
 */
if (!w.__esaTelemetryBooted) {
  w.__esaTelemetryBooted = true;

  window.addEventListener('error', (event) => {
    trackError(event.error ?? event.message, 'window');
  });
  window.addEventListener('unhandledrejection', (event) => {
    trackError(event.reason, 'promise');
  });

  /**
   * TEACH TELEMETRY HOW TO AUTHENTICATE, before starting its timer.
   *
   * `telemetry.ts` deliberately does not go through `request()` — `api.ts`
   * imports `trackError` from it, so importing `session` back would close a
   * module cycle. It takes an injected getter instead, and its own comment
   * says main.tsx owns both and calls this at boot.
   *
   * It never did. `setTelemetryAuth` had exactly one reference in the whole
   * repository: its own definition. So `bearer` stayed null, every flush went
   * out with no Authorization header, and `/api/v1/telemetry` — correctly not
   * a public path — answered 401 to all of it.
   *
   * Not a degradation. Telemetry has never worked in any environment: every
   * page view, action and error report was produced, buffered, sent, refused
   * and dropped, which is why the usage screen had nothing in it.
   */
  setTelemetryAuth(() => session.token);
  initTelemetry();
}

w.__esaRoot.render(
  <StrictMode>
    <QueryClientProvider client={qc}>
      <TooltipProvider>
        <ToastProvider>
          <BrowserRouter>
            <AppErrorBoundary>
              <RouteTelemetry />
              <Boot />
            </AppErrorBoundary>
          </BrowserRouter>
        </ToastProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </StrictMode>,
);
